import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, TrendingUp, Clock, ArrowRight, Download } from 'lucide-react';

const FliersHome = () => {
  const fliers = [
    {
      id: 'chairs-filled',
      title: 'Keep Your Chairs Filled',
      subtitle: 'Automate The Rest',
      description: 'AI-powered marketing that fills your schedule automatically. Perfect for practices struggling with empty appointment slots.',
      stats: ['+34% Rebooking Rate', '127% ROI', '10hrs Saved'],
      icon: <FileText className="w-8 h-8" />,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      link: '/flier.html',
      bestFor: 'Practices with empty appointment slots'
    },
    {
      id: 'revenue',
      title: 'Stop Losing Revenue',
      subtitle: 'Grow Automatically',
      description: 'Turn missed appointments into recurring revenue with AI-powered client retention. Focused on ROI and revenue growth.',
      stats: ['$47K Revenue Increase', '127% ROI', '2.3x CLV'],
      icon: <TrendingUp className="w-8 h-8" />,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      link: '/flier-revenue.html',
      bestFor: 'Practices focused on revenue recovery'
    },
    {
      id: 'time',
      title: 'Get Your Time Back',
      subtitle: 'AI Does The Work',
      description: 'Stop spending hours on marketing. Focus on what you do best—treating clients. Automated workflows that save time.',
      stats: ['10hrs Saved/Week', '95% Less Manual Tasks', '24/7 Automation'],
      icon: <Clock className="w-8 h-8" />,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-800',
      link: '/flier-time.html',
      bestFor: 'Practices overwhelmed with manual marketing'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#f6ffe6] to-[#c4ffa0]/30 py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2d4a1e] mb-6">
                Med Spa Marketing Fliers
              </h1>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                Choose the perfect flier for your practice. Each one is designed with unique messaging 
                to resonate with different pain points and goals.
              </p>
            </div>
          </div>
        </section>

        {/* Fliers Grid */}
        <section className="py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {fliers.map((flier) => (
                <div
                  key={flier.id}
                  className={`${flier.bgColor} rounded-2xl p-8 border-2 ${flier.borderColor} hover:shadow-xl transition-all duration-300 flex flex-col`}
                >
                  {/* Icon & Title */}
                  <div className="mb-6">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br ${flier.color} text-white mb-4`}>
                      {flier.icon}
                    </div>
                    <h2 className={`text-2xl font-bold ${flier.textColor} mb-2`}>
                      {flier.title}
                    </h2>
                    <p className={`text-lg font-semibold ${flier.textColor} opacity-80 mb-4`}>
                      {flier.subtitle}
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      {flier.description}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="mb-6 flex-grow">
                    <div className="space-y-2">
                      {flier.stats.map((stat, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-700">
                          <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${flier.color} mr-3`}></div>
                          <span className="font-medium">{stat}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Best For */}
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Best For
                    </p>
                    <p className="text-sm text-gray-700">
                      {flier.bestFor}
                    </p>
                  </div>

                  {/* CTA Button */}
                  <a
                    href={flier.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center justify-center w-full px-6 py-3 rounded-xl bg-gradient-to-br ${flier.color} text-white font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105 group`}
                  >
                    <span>View Flier</span>
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              ))}
            </div>

            {/* Info Section */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <div className="flex items-start gap-4">
                <Download className="w-6 h-6 text-[#2d4a1e] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    How to Use These Fliers
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-[#7ed957] mr-2">•</span>
                      <span>Click "View Flier" to open the full-size version in a new tab</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#7ed957] mr-2">•</span>
                      <span>Use your browser's print function (Cmd/Ctrl + P) to print</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#7ed957] mr-2">•</span>
                      <span>Fliers are sized for standard 8.5" x 11" paper</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#7ed957] mr-2">•</span>
                      <span>Each flier includes a QR code linking to your signup page</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#7ed957] mr-2">•</span>
                      <span>Choose the flier that best matches your target audience's pain points</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FliersHome;

